import ImageUpload from '@/components/ImageUpload';
import { apiOcrIDCARD, apiOcrIDCARD_BACK } from '@/network/api/api';
import { ProFormDateRangePicker } from '@ant-design/pro-components';
import $idCardBack from '@assets/form/id-card-back.png';
import $idCardFront from '@assets/form/id-card-front.png';
import type { FormInstance } from 'antd';
import { Form, Image, Input, Modal, Radio, Select, Space, message } from 'antd';
import FormItem from 'antd/es/form/FormItem';
import dayjs from 'dayjs';
import { useState } from 'react';
import { DocumentTypeEnum, ExpirationDateTypeEnum } from '../constants';

const IMG_URL = import.meta.env.VITE_IMAGE_URL;

interface LegalPersonFormProps {
  formRef: FormInstance;
  onFinish: (title?: string) => void;
  require2: boolean;
  setRequire2: (v: boolean) => void;
}

const LegalPersonForm: React.FC<LegalPersonFormProps> = ({
  formRef,
  onFinish,
  require2,
  setRequire2,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, contextHolderModal] = Modal.useModal();
  const [status2, setStatus2] = useState(true);
  const [status3, setStatus3] = useState(true);

  return (
    <>
      {contextHolder}
      {contextHolderModal}
      <Form layout="vertical" form={formRef} size="large" onFinish={() => onFinish('法人信息')}>
        <FormItem
          label="证件类型"
          name="documentType"
          rules={[{ required: true }]}
          initialValue={DocumentTypeEnum[0].value}
        >
          <Select options={DocumentTypeEnum} placeholder="请选择" />
        </FormItem>
        <FormItem
          label={
            <>
              <span className="tip">*</span>证件照片上传
            </>
          }
        >
          <Space direction="vertical">
            <div style={{ color: '#979797', fontSize: 12 }}>
              请按照指引上传对应的图片：
              <br />
              1、请上传证件彩色照片，证照需要正面拍摄，露出证件四角且清晰、完整，所有字符清晰可识别，不得反光或遮挡，不得翻拍、截图、镜像、PS。
              <br />
              2、图片只支持 JPG JPEG PNG 格式，文件大小建议 1M，不能超过 5M。
              <br />
              3、身份证支持自动识别。
            </div>
            <Space size={100} align="start">
              <FormItem name={['documentImageUrl', 1]} noStyle>
                <ImageUpload
                  tips="上传人像面"
                  maxCount={1}
                  defaultValue={formRef.getFieldValue('documentImageUrl')?.[1]}
                  size={5}
                  setStatus={() => setStatus3(true)}
                  onChange={(e) => {
                    if (e && status3) {
                      setStatus3(false);
                      messageApi.open({
                        type: 'loading',
                        content: '识别中...',
                        duration: 0,
                      });
                      // 图片识别
                      apiOcrIDCARD(encodeURIComponent(IMG_URL + e))
                        .then(async (res: any) => {
                          if (res.code == 20000 && res.data && Object.keys(res.data).length) {
                            const oldData = formRef.getFieldsValue();
                            const data = res.data;
                            const obj: any = {};
                            const objStay: any = {};
                            const keys: any = {
                              name: 'legalRepresentativeName',
                              id_number: 'documentNumber',
                            };
                            Object.keys(keys).forEach((key) => {
                              const field = keys[key];
                              if (data[key]) {
                                if (oldData[field] && oldData[field] !== data[key]) {
                                  objStay[field] = data[key];
                                } else {
                                  obj[field] = data[key];
                                }
                              }
                            });
                            let b = false;
                            if (Object.keys(objStay).length) {
                              b = await modal.confirm({
                                content: '已有已输入信息，是否覆盖？',
                                okText: '确定',
                                cancelText: '取消',
                              });
                            } else {
                              message.success('识别成功，已自动填充');
                            }
                            formRef.setFieldsValue({ ...obj, ...(b ? objStay : {}) });
                          } else {
                            message.info('识别失败，请重新上传');
                          }
                        })
                        .finally(() => {
                          messageApi.destroy();
                        });
                    }
                  }}
                />
              </FormItem>
              <Space direction="vertical" align="center">
                <Image
                  src={$idCardBack}
                  style={{ width: 148, height: 102, padding: 4, border: '1px solid rgba(0, 0, 0, .15)', borderRadius: 8 }}
                  preview={false}
                />
                示例图
              </Space>
            </Space>
            <Space size={100} align="start">
              <FormItem
                name={['documentImageUrl', 0]}
                rules={[
                  {
                    required: true,
                    validator: () => {
                      const url = formRef.getFieldValue('documentImageUrl');
                      if (url?.length == 2 && url[0] && url[1]) {
                        return Promise.resolve();
                      }
                      return Promise.reject('请上传人像国徽面');
                    },
                  },
                ]}
                noStyle
              >
                <ImageUpload
                  tips="上传国徽面"
                  maxCount={1}
                  defaultValue={formRef.getFieldValue('documentImageUrl')?.[0]}
                  size={5}
                  setStatus={() => setStatus2(true)}
                  onChange={(e) => {
                    if (e && status2) {
                      setStatus2(false);
                      messageApi.open({
                        type: 'loading',
                        content: '识别中...',
                        duration: 0,
                      });
                      // 图片识别
                      apiOcrIDCARD_BACK(encodeURIComponent(IMG_URL + e))
                        .then(async (res: any) => {
                          if (res.code == 20000 && res.data && Object.keys(res.data).length) {
                            const oldData = formRef.getFieldsValue();
                            const data = res.data;
                            const obj: any = {};
                            const objStay: any = {};
                            const keys: any = {
                              dateType: 'documentExpirationDateType',
                            };
                            // 构造数据
                            data.dateType = oldData.documentExpirationDateType;
                            data.date2 = oldData.documentDate;
                            const sum = data.date || [];
                            const [m, n] = [dayjs(sum[0]), dayjs(sum[1])];
                            if (sum.length === 2 && m.isValid() && n.isValid()) {
                              data.dateType = ExpirationDateTypeEnum[1].value;
                              data.date2 = [m, n];
                            } else if (JSON.stringify(sum).includes('长期')) {
                              data.dateType = ExpirationDateTypeEnum[0].value;
                              data.date2 = [];
                            }
                            // 日期单独处理
                            if (data.date) {
                              if (
                                oldData.documentDate?.length &&
                                !(
                                  oldData.documentDate[0].isSame(data.date2[0], 'day') &&
                                  oldData.documentDate[1].isSame(data.date2[1], 'day')
                                )
                              ) {
                                objStay.documentDate = data.date2;
                              } else {
                                obj.documentDate = data.date2;
                              }
                            }
                            // 其它数据处理
                            Object.keys(keys).forEach((key) => {
                              const field = keys[key];
                              if (data[key]) {
                                if (oldData[field] && oldData[field] !== data[key]) {
                                  objStay[field] = data[key];
                                } else {
                                  obj[field] = data[key];
                                }
                              }
                            });
                            // 数据覆盖提示
                            let b = false;
                            if (Object.keys(objStay).length) {
                              b = await modal.confirm({
                                content: '已有已输入信息，是否覆盖？',
                                okText: '确定',
                                cancelText: '取消',
                              });
                            } else {
                              message.success('识别成功，已自动填充');
                            }
                            formRef.setFieldsValue({ ...obj, ...(b ? objStay : {}) });
                            setRequire2(
                              formRef.getFieldValue('documentExpirationDateType') == ExpirationDateTypeEnum[1].value,
                            );
                          } else {
                            message.info('识别失败，请重新上传');
                          }
                        })
                        .finally(() => {
                          messageApi.destroy();
                        });
                    }
                  }}
                />
              </FormItem>
              <Space direction="vertical" align="center">
                <Image
                  src={$idCardFront}
                  style={{ width: 148, height: 102, padding: 4, border: '1px solid rgba(0, 0, 0, .15)', borderRadius: 8 }}
                  preview={false}
                />
                示例图
              </Space>
            </Space>
          </Space>
        </FormItem>
        <FormItem label="法人名称" name="legalRepresentativeName" rules={[{ required: true }, { max: 10 }]}>
          <Input placeholder="请输入" />
        </FormItem>
        <FormItem label="证件号码" name="documentNumber" rules={[{ required: true }]}>
          <Input placeholder="请输入" />
        </FormItem>
        <div className="overlap-date-picker">
          <ProFormDateRangePicker
            label="身份证有效期"
            name="documentDate"
            rules={[{ required: require2, message: '请输入' }]}
            disabled={!require2}
            fieldProps={{ style: { width: '100%' } }}
          />
          <FormItem
            name="documentExpirationDateType"
            rules={[{ required: true, message: '请输入' }]}
            className="overlap-date-type"
            initialValue={ExpirationDateTypeEnum[1].value}
          >
            <Radio.Group
              options={ExpirationDateTypeEnum}
              onChange={(e) => {
                setRequire2(e.target.value == ExpirationDateTypeEnum[1].value);
              }}
            />
          </FormItem>
        </div>
      </Form>
    </>
  );
};

export default LegalPersonForm;
